import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, Euro, Zap, BookmarkPlus, Bookmark, Send, ShieldCheck, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";

export default function ProfessionalHome() {
    const nav = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [prof, setProf] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        date_from: "", date_to: "", min_compensation: "", max_distance_km: "",
    });

    const load = async (overrideFilters) => {
        setLoading(true);
        try {
            const eff = overrideFilters ?? filters;
            const params = new URLSearchParams();
            if (eff.date_from) params.append("date_from", eff.date_from);
            if (eff.date_to) params.append("date_to", eff.date_to);
            if (eff.min_compensation) params.append("min_compensation", eff.min_compensation);
            if (eff.max_distance_km) params.append("max_distance_km", eff.max_distance_km);
            const qs = params.toString();
            const [feed, me] = await Promise.all([
                api.get(`/matching/feed${qs ? "?" + qs : ""}`),
                api.get("/professionals/me")
            ]);
            setJobs(feed.data.data || []);
            setProf(me.data.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const applyFilters = () => load();
    const clearFilters = () => {
        const empty = { date_from: "", date_to: "", min_compensation: "", max_distance_km: "" };
        setFilters(empty);
        load(empty);
    };
    const hasFilters = Object.values(filters).some(v => v !== "");

    const apply = async (job_id) => {
        try {
            await api.post("/applications", { job_id });
            toast.success("Candidatura inviata!");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };
    const toggleSave = async (job) => {
        try {
            if (job.saved) await api.post(`/matching/unsave/${job.id}`);
            else await api.post(`/matching/save/${job.id}`);
            load();
        } catch {}
    };

    const profileIncomplete = !prof?.profession || !prof?.city;

    return (
        <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Opportunità per te</h1>
                    <p className="text-sm text-[#5C5C5C] mt-1">Ordinate per compatibilità</p>
                </div>
                <div className="flex items-center gap-3">
                    <Badge variant="outline" className="hidden md:flex" data-testid="feed-count-badge">{jobs.length} annunci</Badge>
                    <Button variant="outline" size="sm" onClick={() => setShowFilters(v => !v)}
                        data-testid="toggle-filters-btn">
                        <SlidersHorizontal className="w-4 h-4 mr-2" />
                        Filtri {hasFilters && <span className="ml-1 w-2 h-2 rounded-full bg-[#D96C5B] inline-block" />}
                    </Button>
                </div>
            </div>

            {showFilters && (
                <div className="hl-card p-5 mb-6 grid md:grid-cols-4 gap-3" data-testid="feed-filters">
                    <div>
                        <Label className="text-xs">Da</Label>
                        <Input type="date" value={filters.date_from}
                            onChange={(e) => setFilters(f => ({...f, date_from: e.target.value}))}
                            data-testid="filter-date-from" />
                    </div>
                    <div>
                        <Label className="text-xs">A</Label>
                        <Input type="date" value={filters.date_to}
                            onChange={(e) => setFilters(f => ({...f, date_to: e.target.value}))}
                            data-testid="filter-date-to" />
                    </div>
                    <div>
                        <Label className="text-xs">Compenso min (€)</Label>
                        <Input type="number" min={0} value={filters.min_compensation}
                            onChange={(e) => setFilters(f => ({...f, min_compensation: e.target.value}))}
                            data-testid="filter-min-comp" />
                    </div>
                    <div>
                        <Label className="text-xs">Distanza max (km)</Label>
                        <Input type="number" min={0} value={filters.max_distance_km}
                            onChange={(e) => setFilters(f => ({...f, max_distance_km: e.target.value}))}
                            data-testid="filter-max-dist" />
                    </div>
                    <div className="md:col-span-4 flex justify-end gap-2">
                        {hasFilters && (
                            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                                <X className="w-4 h-4 mr-1" />Pulisci
                            </Button>
                        )}
                        <Button size="sm" onClick={applyFilters} className="bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="apply-filters-btn">
                            Applica filtri
                        </Button>
                    </div>
                </div>
            )}

            {profileIncomplete && (
                <div className="hl-card p-5 mb-6 border-[#D96C5B]/40 bg-[#FDF4F2]" data-testid="profile-incomplete-banner">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <div className="font-semibold">Completa il profilo</div>
                            <div className="text-sm text-[#5C5C5C]">Aggiungi professione, città e disponibilità per ricevere match più precisi.</div>
                        </div>
                        <Link to="/app/profilo">
                            <Button className="bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="complete-profile-btn">Completa profilo</Button>
                        </Link>
                    </div>
                </div>
            )}

            {loading && <div className="text-sm text-[#5C5C5C]">Caricamento…</div>}
            {!loading && jobs.length === 0 && (
                <div className="hl-card p-10 text-center">
                    <div className="font-display text-xl">Nessuna opportunità al momento</div>
                    <div className="text-sm text-[#5C5C5C] mt-2">Torna a controllare più tardi.</div>
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
                {jobs.map((j) => (
                    <div key={j.id} className="hl-card p-6" data-testid={`job-card-${j.id}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                                {j.facility_logo_url && (
                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${j.facility_logo_url}`}
                                        alt="" className="w-12 h-12 rounded-md object-cover border border-[#E5E5E0]" />
                                )}
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="hl-pill">{j.profession}</span>
                                        {j.urgent && (
                                            <span className="hl-pill bg-[#FDF4F2] text-[#D96C5B] border-[#D96C5B]/30">
                                                <Zap className="w-3 h-3" /> Urgente
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="mt-3 font-display text-lg font-bold tracking-tight">{j.title}</h3>
                                    <div className="text-sm text-[#5C5C5C] flex items-center gap-1">
                                        {j.facility_name}
                                        {j.facility_verified && (
                                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" data-testid={`verified-facility-${j.id}`} />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-xs text-[#5C5C5C]">Compatibilità</div>
                                <div className="font-display text-2xl font-bold text-[#1C3F39]">{j.match_score}%</div>
                            </div>
                        </div>
                        <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2 text-[#5C5C5C]">
                                <MapPin className="w-4 h-4" />
                                {j.city}{j.distance_km != null && ` · ${j.distance_km} km`}
                            </div>
                            <div className="flex items-center gap-2 text-[#5C5C5C]">
                                <Clock className="w-4 h-4" /> {j.date} · {j.start_time}–{j.end_time}
                            </div>
                            <div className="flex items-center gap-2 text-[#1A1A1A] font-semibold col-span-2">
                                <Euro className="w-4 h-4" /> <span className="font-mono">{j.compensation_gross?.toFixed(2)}€</span> <span className="text-xs font-normal text-[#5C5C5C]">lordo</span>
                            </div>
                        </div>
                        <div className="mt-5 flex items-center gap-2">
                            <Button onClick={() => apply(j.id)} disabled={j.already_applied}
                                className="bg-[#1C3F39] hover:bg-[#2A5C53]"
                                data-testid={`apply-btn-${j.id}`}>
                                <Send className="w-4 h-4 mr-2" />
                                {j.already_applied ? "Candidato" : "Candidati"}
                            </Button>
                            <Button variant="outline" onClick={() => nav(`/app/job/${j.id}`)} data-testid={`view-job-${j.id}`}>Dettagli</Button>
                            <Button variant="ghost" size="icon" onClick={() => toggleSave(j)} data-testid={`save-job-${j.id}`}>
                                {j.saved ? <Bookmark className="w-4 h-4 fill-current" /> : <BookmarkPlus className="w-4 h-4" />}
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
